/**
 * GPU-driven 3D typhoon particle system for Cesium (WebGL).
 *
 * Architecture (see shaders.js for the GLSL):
 *  - The wind field is an ANALYTIC vortex computed directly in the shaders
 *    (Lamb-Oseen swirl + radial inflow/outflow + eyewall updraft), so no
 *    wind texture is needed anymore (windData.js is obsolete).
 *  - Particle state lives in a floating-point texture. A full-screen "compute"
 *    pass (a ComputeCommand executed by Cesium's ComputeEngine) advects every
 *    particle through the wind field and writes the result into a second
 *    texture (classic ping-pong GPGPU). No CPU readback happens.
 *  - A single GL_POINTS draw call renders all particles: the vertex shader
 *    samples the particle texture to recover each position, converts it to
 *    ECEF and projects it with czm_modelViewProjection.
 *
 * The class is a Cesium primitive that registers itself with
 * `scene.primitives` on construction.
 */

/* global Cesium */
import {
  computeFragmentShader,
  renderVertexShader,
  renderFragmentShader,
  heatVertexShader,
  heatFragmentShader
} from "./shaders";

const DEG2RAD = Math.PI / 180;

function pick(value, fallback) {
  return value === undefined || value === null ? fallback : value;
}

// Strip GLSL comments before passing to Cesium to work around a
// removeComments null-reference bug in some Cesium builds.
function stripGLSL(s) {
  return s.replace(/\/\*[\s\S]*?\*\//g, "").replace(/\/\/[^\n]*/g, "");
}

// IEEE-754 single -> half precision (used only if float render targets fail).
function floatToHalfBits(value) {
  const f32 = new Float32Array(1);
  const u32 = new Uint32Array(f32.buffer);
  f32[0] = value;
  const x = u32[0];
  const bits = (x >> 16) & 0x8000;
  let m = (x >> 12) & 0x07ff;
  const e = (x >> 23) & 0xff;
  if (e < 103) return bits;
  if (e > 142) {
    return bits | 0x7c00 | ((e === 255 ? 0 : 1) && (x & 0x007fffff));
  }
  if (e < 113) {
    m |= 0x0800;
    return bits | (m >> (114 - e)) + ((m >> (113 - e)) & 1);
  }
  return bits | ((e - 112) << 10) | (m >> 1) + (m & 1);
}

class WindParticles {
  constructor(options = {}) {
    this.viewer = options.viewer;
    this.scene = this.viewer.scene;
    this._context = this.scene.context;

    // Geographic domain of the simulation (degrees / meters).
    this.domain = pick(options.domain, {
      minLon: 109.5,
      maxLon: 110.5,
      minLat: 34.5,
      maxLat: 35.5,
      minH: 0,
      maxH: 960000
    });

    // Particle texture resolution (particle count = texSize^2).
    this._texSize = pick(options.particleTexSize, 128);

    // Live-tunable parameters (read every frame through uniform closures).
    this.speedFactor = pick(options.speedFactor, 0.20);
    this.lifeDecay = pick(options.lifeDecay, 0.05);
    this.verticalScale = pick(options.verticalScale, 1.5);
    this.pointSize = pick(options.pointSize, 3.5);
    this.opacity = pick(options.opacity, 1.0);
    this.colorSlow = pick(
      options.colorSlow,
      new Cesium.Cartesian3(0.24, 0.55, 1.0)
    );
    this.colorFast = pick(
      options.colorFast,
      new Cesium.Cartesian3(0.76, 1.0, 1.0)
    );

    // Analytic vortex parameters (normalised domain space).
    this.vortexRadius = pick(options.vortexRadius, 0.25);
    this.swirlStrength = pick(options.swirlStrength, 0.45);
    this.inflowStrength = pick(options.inflowStrength, 0.12);
    this.updraftStrength = pick(options.updraftStrength, 0.35);
    this.environmentStrength = pick(options.environmentStrength, 0.06);
    this.trailLength = pick(options.trailLength, 8.0);

    // Wind-field mode: "vortex" (typhoon) or "regional" (realistic synoptic flow).
    this.fieldType = pick(options.fieldType, "vortex");
    this.regionalBaseSpeed = pick(options.regionalBaseSpeed, 0.9);
    this.regionalMeander = pick(options.regionalMeander, 0.8);
    this.regionalUpdraft = pick(options.regionalUpdraft, 0.3);
    this.regionalTurbulence = pick(options.regionalTurbulence, 0.6);

    // Static arc mode: particles become fixed wind-direction arcs (no advection).
    this.arcMode = pick(options.arcMode, false);
    this.arcHeight = pick(options.arcHeight, 0.1);

    this.show = true;
    this.isRunning = true;
    this._ready = false;
    this._destroyed = false;

    this._time = 0;
    this._dt = 0.016;
    this._lastJulian = null;

    this._build();
  }

  /* ---------------------------------------------------------------- */
  /* Construction                                                      */
  /* ---------------------------------------------------------------- */
  _build() {
    this._texSizeCart = new Cesium.Cartesian2(this._texSize, this._texSize);

    const d = this.domain;
    this._lonLatRange = new Cesium.Cartesian4(
      d.minLon * DEG2RAD,
      d.minLat * DEG2RAD,
      d.maxLon * DEG2RAD,
      d.maxLat * DEG2RAD
    );
    this._heightRange = new Cesium.Cartesian2(d.minH, d.maxH);

    this._computeBoundingSphere();
    this._createComputeResources();
    this._createRenderResources();
    this._createHeatResources();
    this._createParticleTargets(this._texSize);

    if (this._ready) {
      this.viewer.scene.primitives.add(this);
    }
  }

  _computeBoundingSphere() {
    const d = this.domain;
    const corners = [];
    [d.minLon, d.maxLon].forEach(lo =>
      [d.minLat, d.maxLat].forEach(la =>
        [d.minH, d.maxH].forEach(h =>
          corners.push(Cesium.Cartesian3.fromDegrees(lo, la, h))
        )
      )
    );
    const center = new Cesium.Cartesian3();
    corners.forEach(c => Cesium.Cartesian3.add(center, c, center));
    Cesium.Cartesian3.multiplyByScalar(center, 1 / corners.length, center);
    let radius = 0;
    corners.forEach(c => {
      radius = Math.max(radius, Cesium.Cartesian3.distance(center, c));
    });
    this._boundingSphere = new Cesium.BoundingSphere(center, radius * 1.15);
  }

  _createComputeResources() {
    this._computeUniforms = {
      u_particleTexture: () => this._frameReadTex,
      u_texSize: () => this._texSizeCart,
      u_time: () => this._time,
      u_dt: () => this._dt,
      u_speedFactor: () => this.speedFactor,
      u_lifeDecay: () => this.lifeDecay,
      u_verticalScale: () => this.verticalScale,
      u_vortexRadius: () => this.vortexRadius,
      u_swirlStrength: () => this.swirlStrength,
      u_inflowStrength: () => this.inflowStrength,
      u_updraftStrength: () => this.updraftStrength,
      u_environmentStrength: () => this.environmentStrength,
      u_fieldType: () => (this.fieldType === "regional" ? 1 : 0),
      u_regionalBaseSpeed: () => this.regionalBaseSpeed,
      u_regionalMeander: () => this.regionalMeander,
      u_regionalUpdraft: () => this.regionalUpdraft,
      u_regionalTurbulence: () => this.regionalTurbulence
    };

    // This Cesium build routes Pass.COMPUTE commands through its ComputeEngine,
    // which renders a full-screen quad into `outputTexture` with a viewport
    // sized to that texture (depth test / blending default to off). So the
    // advection step is expressed as a ComputeCommand-shaped object instead of
    // a raw DrawCommand.
    this._computeCommand = {
      pass: Cesium.Pass.COMPUTE,
      fragmentShaderSource: stripGLSL(computeFragmentShader),
      uniformMap: this._computeUniforms,
      outputTexture: null, // assigned per frame in update()
      persists: true, // keep the cached shader program between frames
      execute(engine) {
        engine.execute(this);
      }
    };
  }

  _createRenderResources() {
    const ctx = this._context;

    this._renderSP = Cesium.ShaderProgram.fromCache({
      context: ctx,
      vertexShaderSource: stripGLSL(renderVertexShader),
      fragmentShaderSource: stripGLSL(renderFragmentShader),
      attributeLocations: { a_particleUV: 0 }
    });

    this._renderRS = Cesium.RenderState.fromCache({
      depthTest: { enabled: true, func: Cesium.WebGLConstants.LEQUAL },
      depthMask: false,
      blending: {
        enabled: true,
        colorEquation: Cesium.WebGLConstants.FUNC_ADD,
        colorFunction: {
          source: Cesium.WebGLConstants.SRC_ALPHA,
          destination: Cesium.WebGLConstants.ONE
        },
        alphaEquation: Cesium.WebGLConstants.FUNC_ADD,
        alphaFunction: {
          source: Cesium.WebGLConstants.ONE,
          destination: Cesium.WebGLConstants.ONE
        }
      },
      cull: { enabled: false }
    });

    this._renderUniforms = {
      u_particleTexture: () => this._frameRenderTex,
      u_lonLatRange: () => this._lonLatRange,
      u_heightRange: () => this._heightRange,
      u_pointSize: () => this.pointSize,
      u_vortexRadius: () => this.vortexRadius,
      u_swirlStrength: () => this.swirlStrength,
      u_inflowStrength: () => this.inflowStrength,
      u_updraftStrength: () => this.updraftStrength,
      u_environmentStrength: () => this.environmentStrength,
      u_fieldType: () => (this.fieldType === "regional" ? 1 : 0),
      u_regionalBaseSpeed: () => this.regionalBaseSpeed,
      u_regionalMeander: () => this.regionalMeander,
      u_regionalUpdraft: () => this.regionalUpdraft,
      u_regionalTurbulence: () => this.regionalTurbulence,
      u_verticalScale: () => this.verticalScale,
      u_speedFactor: () => this.speedFactor,
      u_trailLength: () => this.trailLength,
      u_time: () => this._time,
      u_arcMode: () => (this.arcMode ? 1 : 0),
      u_arcHeight: () => this.arcHeight,
      u_colorSlow: () => this.colorSlow,
      u_colorFast: () => this.colorFast,
      u_opacity: () => this.opacity
    };

    this._renderCommand = new Cesium.DrawCommand({
      primitiveType: Cesium.PrimitiveType.POINTS,
      shaderProgram: this._renderSP,
      uniformMap: this._renderUniforms,
      renderState: this._renderRS,
      pass: Cesium.Pass.TRANSLUCENT,
      modelMatrix: Cesium.Matrix4.IDENTITY,
      boundingVolume: this._boundingSphere,
      cull: true
    });
  }

  /**
   * Builds the bounding-cube geometry + DrawCommand used by the ray-marched
   * 3D heatmap (arc mode). The cube is expressed in [0,1]^3 domain-local
   * coords; the vertex shader lifts each vertex to ECEF via geodeticToEcef.
   * Winding is CCW-from-outside so BACK-face culling renders only the
   * entry faces the camera sees.
   */
  _createHeatResources() {
    const ctx = this._context;

    // 12 triangles, 36 vertices, each carrying a vec3 local position.
    const corners = new Float32Array([
      // +X face
      1, 0, 0, 1, 1, 0, 1, 1, 1,  1, 0, 0, 1, 1, 1, 1, 0, 1,
      // -X face
      0, 0, 0, 0, 1, 1, 0, 1, 0,  0, 0, 0, 0, 0, 1, 0, 1, 1,
      // +Y face
      0, 1, 0, 1, 1, 1, 1, 1, 0,  0, 1, 0, 0, 1, 1, 1, 1, 1,
      // -Y face
      0, 0, 0, 1, 0, 0, 1, 0, 1,  0, 0, 0, 1, 0, 1, 0, 0, 1,
      // +Z face
      0, 0, 1, 1, 0, 1, 1, 1, 1,  0, 0, 1, 1, 1, 1, 0, 1, 1,
      // -Z face
      0, 0, 0, 1, 1, 0, 1, 0, 0,  0, 0, 0, 0, 1, 0, 1, 1, 0
    ]);

    this._heatBuffer = Cesium.Buffer.createVertexBuffer({
      context: ctx,
      typedArray: corners,
      usage: Cesium.WebGLConstants.STATIC_DRAW
    });
    this._heatVA = new Cesium.VertexArray({
      context: ctx,
      attributes: [
        {
          index: 0,
          vertexBuffer: this._heatBuffer,
          componentsPerAttribute: 3,
          componentDatatype: Cesium.WebGLConstants.FLOAT
        }
      ]
    });

    this._heatSP = Cesium.ShaderProgram.fromCache({
      context: ctx,
      vertexShaderSource: stripGLSL(heatVertexShader),
      fragmentShaderSource: stripGLSL(heatFragmentShader),
      attributeLocations: { a_localPos: 0 }
    });

    // Premultiplied alpha blending for correct front-to-back volume compositing.
    this._heatRS = Cesium.RenderState.fromCache({
      depthTest: { enabled: true, func: Cesium.WebGLConstants.LEQUAL },
      depthMask: false,
      blending: {
        enabled: true,
        colorEquation: Cesium.WebGLConstants.FUNC_ADD,
        colorFunction: {
          source: Cesium.WebGLConstants.ONE,
          destination: Cesium.WebGLConstants.ONE_MINUS_SRC_ALPHA
        },
        alphaEquation: Cesium.WebGLConstants.FUNC_ADD,
        alphaFunction: {
          source: Cesium.WebGLConstants.ONE,
          destination: Cesium.WebGLConstants.ONE_MINUS_SRC_ALPHA
        }
      },
      cull: { enabled: true, face: Cesium.WebGLConstants.BACK }
    });

    // Camera position in domain-local [0,1]^3 space, recomputed every frame.
    this._cameraLocal = new Cesium.Cartesian3(0.5, 0.5, 2.0);

    this._heatUniforms = {
      u_lonLatRange: () => this._lonLatRange,
      u_heightRange: () => this._heightRange,
      u_cameraLocal: () => this._cameraLocal,
      u_time: () => this._time,
      u_fieldType: () => (this.fieldType === "regional" ? 1 : 0),
      u_vortexRadius: () => this.vortexRadius,
      u_swirlStrength: () => this.swirlStrength,
      u_inflowStrength: () => this.inflowStrength,
      u_updraftStrength: () => this.updraftStrength,
      u_environmentStrength: () => this.environmentStrength,
      u_regionalBaseSpeed: () => this.regionalBaseSpeed,
      u_regionalMeander: () => this.regionalMeander,
      u_regionalUpdraft: () => this.regionalUpdraft,
      u_regionalTurbulence: () => this.regionalTurbulence,
      u_verticalScale: () => this.verticalScale,
      u_opacity: () => this.opacity,
      u_arcHeight: () => this.arcHeight
    };

    this._heatCommand = new Cesium.DrawCommand({
      primitiveType: Cesium.PrimitiveType.TRIANGLES,
      shaderProgram: this._heatSP,
      uniformMap: this._heatUniforms,
      renderState: this._heatRS,
      pass: Cesium.Pass.TRANSLUCENT,
      modelMatrix: Cesium.Matrix4.IDENTITY,
      boundingVolume: this._boundingSphere,
      cull: true,
      vertexArray: this._heatVA,
      count: 36
    });
  }

  /**
   * Updates u_cameraLocal: converts the Cesium camera's ECEF position to
   * domain-local [0,1]^3 parameter space. The heat fragment shader uses
   * this as the ray origin for the ray-box intersection.
   */
  _updateCameraLocal() {
    const camPos = this.scene.camera.position;
    const carto = Cesium.Cartographic.fromCartesian(camPos);
    this._cameraLocal.x =
      (carto.longitude - this._lonLatRange.x) /
      (this._lonLatRange.z - this._lonLatRange.x);
    this._cameraLocal.y =
      (carto.latitude - this._lonLatRange.y) /
      (this._lonLatRange.w - this._lonLatRange.y);
    this._cameraLocal.z =
      (carto.height - this._heightRange.x) /
      (this._heightRange.y - this._heightRange.x);
  }

  /**
   * (Re)creates the ping-pong particle textures and the point vertex array
   * for a given particle-texture resolution.
   */
  _createParticleTargets(texSize) {
    const ctx = this._context;
    const n = texSize;

    // 初始位置按风场类型区分：
    // - 台风：40% 落在涡旋盘内，60% 分布在周围环境风区域；
    // - 区域：在整个模拟域内均匀分布，且占满全高度柱。
    const init = new Float32Array(n * n * 4);
    const regional = this.fieldType === "regional";
    for (let i = 0; i < n * n; i++) {
      if (regional) {
        init[i * 4] = 0.02 + Math.random() * 0.96;
        init[i * 4 + 1] = 0.02 + Math.random() * 0.96;
        init[i * 4 + 2] = Math.random();
      } else {
        const angle = Math.random() * Math.PI * 2;
        let r;
        if (Math.random() < 0.4) {
          r = 0.05 + (0.25 - 0.05) * Math.sqrt(Math.random());
        } else {
          r = 0.25 + (0.98 - 0.25) * Math.random();
        }
        init[i * 4] = 0.5 + r * Math.cos(angle);
        init[i * 4 + 1] = 0.5 + r * Math.sin(angle);
        init[i * 4 + 2] = Math.random() * 0.12;
      }
      init[i * 4 + 3] = 0.3 + Math.random() * 0.7;
    }

    // Choose a renderable floating-point datatype.
    let datatype = Cesium.PixelDatatype.FLOAT;
    let viewA = init;
    let probe = this._makeParticleTexture(n, init, Cesium.PixelDatatype.FLOAT);
    if (!probe.complete) {
      probe.texture.destroy();
      const half = new Uint16Array(n * n * 4);
      for (let i = 0; i < init.length; i++) half[i] = floatToHalfBits(init[i]);
      datatype = Cesium.PixelDatatype.HALF_FLOAT;
      viewA = half;
      probe = this._makeParticleTexture(n, half, Cesium.PixelDatatype.HALF_FLOAT);
    }
    if (!probe.complete) {
      console.error(
        "[WindParticles] Floating point render targets are not supported on this GPU; wind field disabled."
      );
      probe.texture.destroy();
      this._ready = false;
      return;
    }

    this._texA = probe.texture;
    this._texB = this._makeParticleTexture(n, viewA, datatype).texture;

    this._readTex = this._texA;
    this._writeTex = this._texB;
    this._frameReadTex = this._texA;
    this._frameRenderTex = this._texA;

    // Point vertex array: one vertex per particle carrying its texture UV.
    const uvs = new Float32Array(n * n * 2);
    let k = 0;
    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        uvs[k++] = (x + 0.5) / n;
        uvs[k++] = (y + 0.5) / n;
      }
    }
    if (this._particleBuffer) this._particleBuffer.destroy();
    if (this._particleVA) this._particleVA.destroy();
    this._particleBuffer = Cesium.Buffer.createVertexBuffer({
      context: ctx,
      typedArray: uvs,
      usage: Cesium.WebGLConstants.STATIC_DRAW
    });
    this._particleVA = new Cesium.VertexArray({
      context: ctx,
      attributes: [
        {
          index: 0,
          vertexBuffer: this._particleBuffer,
          componentsPerAttribute: 2,
          componentDatatype: Cesium.WebGLConstants.FLOAT
        }
      ]
    });

    this._renderCommand.vertexArray = this._particleVA;
    this._renderCommand.count = n * n;
    this._texSize = n;
    this._texSizeCart = new Cesium.Cartesian2(n, n);

    this._ready = true;
  }

  _makeParticleTexture(n, data, datatype) {
    const texture = new Cesium.Texture({
      context: this._context,
      width: n,
      height: n,
      pixelFormat: Cesium.PixelFormat.RGBA,
      pixelDatatype: datatype,
      source: { arrayBufferView: data },
      sampler: new Cesium.Sampler({
        wrapS: Cesium.TextureWrap.CLAMP_TO_EDGE,
        wrapT: Cesium.TextureWrap.CLAMP_TO_EDGE,
        minificationFilter: Cesium.TextureMinificationFilter.NEAREST,
        magnificationFilter: Cesium.TextureMagnificationFilter.NEAREST
      })
    });

    // Probe whether this texture format is renderable on this GPU. The
    // ComputeEngine creates its own framebuffer per pass, so the probe FBO is
    // discarded right away.
    const fbo = new Cesium.Framebuffer({
      context: this._context,
      colorTextures: [texture],
      destroyAttachments: false
    });
    const complete = fbo.status === Cesium.WebGLConstants.FRAMEBUFFER_COMPLETE;
    fbo.destroy();

    return { texture, complete };
  }

  /* ---------------------------------------------------------------- */
  /* Public API                                                        */
  /* ---------------------------------------------------------------- */
  get particleCount() {
    return this._texSize * this._texSize;
  }

  /** Change the number of particles (rebuilds GPU targets). */
  setParticleCount(count) {
    const n = Math.max(
      16,
      Math.min(512, Math.round(Math.sqrt(count)))
    );
    if (n === this._texSize) return;
    if (this._texA) this._texA.destroy();
    if (this._texB) this._texB.destroy();
    this._createParticleTargets(n);
  }

  start() {
    this.isRunning = true;
  }

  stop() {
    this.isRunning = false;
  }

  /* ---------------------------------------------------------------- */
  /* Cesium primitive interface                                        */
  /* ---------------------------------------------------------------- */
  update(frameState) {
    if (this._destroyed || !this._ready || !this.show) return;

    // Frame delta time (clamped for stability).
    let dt = 0.016;
    if (this._lastJulian) {
      dt = Cesium.JulianDate.secondsDifference(frameState.time, this._lastJulian);
      if (dt < 0) dt = 0;
      if (dt > 0.05) dt = 0.05;
    }
    this._lastJulian = Cesium.JulianDate.clone(frameState.time, this._lastJulian);
    this._dt = this.isRunning ? dt : 0;
    if (this.isRunning) this._time += dt;

    // Arc mode = 3D ray-marched heatmap: no particle advection, render the
    // domain cube and let the heat fragment shader march through it.
    if (this.isRunning && !this.arcMode) {
      // Capture this frame's ping-pong targets (closures read these lazily at
      // execution time, so they must not be mutated by the swap below).
      this._frameReadTex = this._readTex;
      this._frameRenderTex = this._writeTex;
      this._computeCommand.outputTexture = this._writeTex;

      frameState.commandList.push(this._computeCommand);

      // Advance ping-pong state for the next frame.
      const t = this._readTex;
      this._readTex = this._writeTex;
      this._writeTex = t;
    } else if (!this.arcMode) {
      // Frozen: render the latest completed texture without recomputing.
      this._frameRenderTex = this._readTex;
    }

    if (this.arcMode) {
      // Refresh camera-local uniform, then push the volume command.
      this._updateCameraLocal();
      frameState.commandList.push(this._heatCommand);
    } else {
      frameState.commandList.push(this._renderCommand);
    }
  }

  isDestroyed() {
    return this._destroyed;
  }

  destroy() {
    const safe = o => {
      if (o && typeof o.destroy === "function" && !o.isDestroyed()) o.destroy();
    };
    safe(this._texA);
    safe(this._texB);
    safe(this._particleVA);
    safe(this._particleBuffer);
    safe(this._renderSP);
    safe(this._heatSP);
    safe(this._heatVA);
    safe(this._heatBuffer);
    this._destroyed = true;
  }
}

export default WindParticles;
